import React from 'react';
import './styles.scss';
import { Typography } from 'Components/atoms';
import { LogicBadge } from 'Components/atoms/LogicBadge';
import { SummaryBadge } from 'Components/atoms/SummaryBadge';
import { SummaryTreeNode } from 'Components/organisms/SummaryTreeNode';
import { BsCalendar3 } from '@react-icons/all-files/bs/BsCalendar3';
import { BsTag } from '@react-icons/all-files/bs/BsTag';
import { BsLightbulb } from '@react-icons/all-files/bs/BsLightbulb';
import { ACTION_DATA_TYPES, ActionEntry, BuilderMode, CondEntry, GroupNode, LogicOp } from 'Core-utils/types/rules.types';
import { condSummaryText } from 'Core-utils/helpers/ruleBuilderHelpers';
import { useI18n } from 'Hooks/useI18n';
import {
    RULE_BUILDER_SIDEBAR_SUMMARY_TITLE,
    RULE_BUILDER_SIDEBAR_SHOW,
    RULE_BUILDER_SIDEBAR_HIDE,
    RULE_BUILDER_SIDEBAR_SUMMARY_CAPTION,
    RULE_BUILDER_SIDEBAR_WHEN_ALL,
    RULE_BUILDER_SIDEBAR_WHEN_ANY,
    RULE_BUILDER_SIDEBAR_WHEN_ADV,
    RULE_BUILDER_SIDEBAR_NO_CONDITIONS,
    RULE_BUILDER_SIDEBAR_HELP_TITLE,
    RULE_BUILDER_SIDEBAR_EDIT_NOTE,
    RULE_BUILDER_SIDEBAR_HELP_WHEN_TITLE,
    RULE_BUILDER_SIDEBAR_HELP_WHEN_BODY,
    RULE_BUILDER_SIDEBAR_HELP_THEN_TITLE,
    RULE_BUILDER_SIDEBAR_HELP_THEN_BODY,
    RULE_BUILDER_SIDEBAR_HELP_ELSE_TITLE,
    RULE_BUILDER_SIDEBAR_HELP_ELSE_BODY,
    RULE_BUILDER_SIDEBAR_LEGEND_TITLE,
    RULE_BUILDER_SIDEBAR_LOGIC_OPS,
    RULE_BUILDER_SIDEBAR_LEGEND_AND,
    RULE_BUILDER_SIDEBAR_LEGEND_OR,
    RULE_BUILDER_SIDEBAR_NESTED_TITLE,
    RULE_BUILDER_SIDEBAR_NESTED_DESC,
    RULE_BUILDER_SIDEBAR_NESTED_DEPTH,
} from 'Core-utils/constants/constants';

export interface RuleSidebarProps {
    mode: BuilderMode;
    simpleBehavior: LogicOp;
    simpleConditions: CondEntry[];
    advancedChildren: Array<CondEntry | GroupNode>;
    thenActions: ActionEntry[];
    elseActions: ActionEntry[];
    hidden: boolean;
    onToggleHide: () => void;
}

const RuleSidebar = ({
    mode,
    simpleBehavior,
    simpleConditions,
    advancedChildren,
    thenActions,
    elseActions,
    hidden,
    onToggleHide,
}: RuleSidebarProps) => {
    const { t } = useI18n();

    const helpItems = [
        {
            icon: <BsCalendar3 size={12} />,
            iconClass: 'rb-sidebar__help-icon-badge--when',
            title: t(RULE_BUILDER_SIDEBAR_HELP_WHEN_TITLE, '1. When'),
            body: t(RULE_BUILDER_SIDEBAR_HELP_WHEN_BODY, 'The rule checks your conditions.'),
        },
        {
            icon: <BsTag size={12} />,
            iconClass: 'rb-sidebar__help-icon-badge--then',
            title: t(RULE_BUILDER_SIDEBAR_HELP_THEN_TITLE, '2. Then'),
            body: t(RULE_BUILDER_SIDEBAR_HELP_THEN_BODY, 'If the conditions are met, the action in Then will run.'),
        },
        {
            icon: <span style={{ fontSize: 13, lineHeight: 1 }}>⊘</span>,
            iconClass: 'rb-sidebar__help-icon-badge--else',
            title: t(RULE_BUILDER_SIDEBAR_HELP_ELSE_TITLE, '3. Otherwise'),
            body: t(RULE_BUILDER_SIDEBAR_HELP_ELSE_BODY, 'If the conditions are not met, the action in Otherwise will run.'),
        },
    ];

    const legendItems: { logic: LogicOp; desc: string }[] = [
        { logic: 'and', desc: t(RULE_BUILDER_SIDEBAR_LEGEND_AND, 'All conditions in the group must be true') },
        { logic: 'or',  desc: t(RULE_BUILDER_SIDEBAR_LEGEND_OR, 'At least one condition in the group must be true') },
    ];

    const thenLabel = ACTION_DATA_TYPES.find((a) => a.value === thenActions[0]?.actionType)?.label ?? '—';
    const elseLabel = ACTION_DATA_TYPES.find((a) => a.value === elseActions[0]?.actionType)?.label ?? '—';

    const whenCaption =
        mode === 'simple'
            ? `(${simpleBehavior === 'and' ? t(RULE_BUILDER_SIDEBAR_WHEN_ALL, 'All') : t(RULE_BUILDER_SIDEBAR_WHEN_ANY, 'Any')} conditions must be true)`
            : t(RULE_BUILDER_SIDEBAR_WHEN_ADV, 'All of the following are true');

    return (
        <div className="rb-sidebar">
            <div className="rb-sidebar__section">
                <div className="d-flex align-items-center justify-content-between mb-1">
                    <Typography variant="body2" className="rb-sidebar__section-title mb-0">
                        {t(RULE_BUILDER_SIDEBAR_SUMMARY_TITLE, 'Rule summary')}
                    </Typography>
                    <button type="button" className="rb-sidebar__hide-btn" onClick={onToggleHide}>
                        {hidden ? t(RULE_BUILDER_SIDEBAR_SHOW, 'Show') : t(RULE_BUILDER_SIDEBAR_HIDE, 'Hide')}
                    </button>
                </div>

                {!hidden && (
                    <>
                        <Typography variant="caption1" color="text-low-emphasis" className="d-block mb-3" style={{ fontSize: 12 }}>
                            {t(RULE_BUILDER_SIDEBAR_SUMMARY_CAPTION, "Here's what will happen.")}
                        </Typography>

                        {/* WHEN */}
                        <div className="rb-sidebar__row mb-2">
                            <SummaryBadge variant="when">WHEN</SummaryBadge>
                            <Typography variant="caption1" color="text-low-emphasis" className="ms-2 mb-0" style={{ fontSize: 11 }}>
                                {whenCaption}
                            </Typography>
                        </div>

                        {mode === 'simple' && (
                            <ul className="rb-sidebar__list">
                                {simpleConditions.map((c) => (
                                    <li key={c.id}>
                                        <Typography variant="caption1" color="text-high-emphasis" className="mb-0" style={{ fontSize: 12, lineHeight: 1.4 }}>
                                            {condSummaryText(c)}
                                        </Typography>
                                    </li>
                                ))}
                                {simpleConditions.length === 0 && (
                                    <li>
                                        <Typography variant="caption1" color="text-low-emphasis" className="mb-0" style={{ fontSize: 12, lineHeight: 1.4 }}>
                                            {t(RULE_BUILDER_SIDEBAR_NO_CONDITIONS, 'No conditions yet')}
                                        </Typography>
                                    </li>
                                )}
                            </ul>
                        )}

                        {mode === 'advanced' && (
                            <div className="rb-summary-tree">
                                {advancedChildren.map((child) => (
                                    <SummaryTreeNode key={child.id} node={child} />
                                ))}
                                {advancedChildren.length === 0 && (
                                    <Typography variant="caption1" color="text-low-emphasis" className="mb-0" style={{ fontSize: 12 }}>
                                        {t(RULE_BUILDER_SIDEBAR_NO_CONDITIONS, 'No conditions yet')}
                                    </Typography>
                                )}
                            </div>
                        )}

                        {/* THEN */}
                        <div className="rb-sidebar__row mt-3 mb-1">
                            <SummaryBadge variant="then">THEN</SummaryBadge>
                        </div>
                        <Typography variant="caption1" color="text-high-emphasis" style={{ fontSize: 12 }} className="ms-1">
                            {thenLabel}
                        </Typography>

                        {/* ELSE */}
                        {elseActions.length > 0 && (
                            <>
                                <div className="rb-sidebar__row mt-3 mb-1">
                                    <SummaryBadge variant="else">ELSE</SummaryBadge>
                                </div>
                                <Typography variant="caption1" color="text-high-emphasis" style={{ fontSize: 12 }} className="ms-1">
                                    {elseLabel}
                                </Typography>
                            </>
                        )}
                    </>
                )}
            </div>

            <div className="rb-sidebar__divider" />

            {mode === 'simple' ? (
                <div className="rb-sidebar__section">
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <BsLightbulb size={14} className="rb-sidebar__help-icon" />
                        <Typography variant="body2" className="rb-sidebar__section-title mb-0">
                            {t(RULE_BUILDER_SIDEBAR_HELP_TITLE, 'How rules work')}
                        </Typography>
                    </div>
                    {helpItems.map(({ icon, iconClass, title, body }) => (
                        <div key={title} className="rb-sidebar__help-item">
                            <div className="d-flex align-items-start gap-2">
                                <span className={`rb-sidebar__help-icon-badge ${iconClass}`}>{icon}</span>
                                <div>
                                    <Typography variant="caption1" color="text-high-emphasis" className="fw-bold mb-0 d-block" style={{ fontSize: 12, lineHeight: 1.4 }}>
                                        {title}
                                    </Typography>
                                    <Typography variant="caption1" color="text-medium-emphasis" className="mb-0" style={{ fontSize: 12, lineHeight: 1.4 }}>
                                        {body}
                                    </Typography>
                                </div>
                            </div>
                        </div>
                    ))}
                    <Typography variant="caption1" color="text-medium-emphasis" className="d-block mt-2" style={{ fontSize: 12 }}>
                        {t(RULE_BUILDER_SIDEBAR_EDIT_NOTE, 'You can edit or update your rule anytime.')}
                    </Typography>
                </div>
            ) : (
                <div className="rb-sidebar__section">
                    <Typography variant="body2" className="rb-sidebar__section-title mb-3">
                        {t(RULE_BUILDER_SIDEBAR_LEGEND_TITLE, 'Legend')}
                    </Typography>
                    <Typography variant="caption1" color="text-medium-emphasis" className="d-block mb-2 mb-0" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {t(RULE_BUILDER_SIDEBAR_LOGIC_OPS, 'Logic operators')}
                    </Typography>
                    {legendItems.map(({ logic, desc }) => (
                        <div key={logic} className="d-flex align-items-center gap-2 mb-2">
                            <LogicBadge logic={logic} />
                            <Typography variant="caption1" color="text-medium-emphasis" className="mb-0" style={{ fontSize: 12, lineHeight: 1.4 }}>
                                {desc}
                            </Typography>
                        </div>
                    ))}
                    <div className="d-flex align-items-start gap-2 mt-1 mb-2">
                        <span className="rb-sidebar__plus-icon">+</span>
                        <div>
                            <Typography variant="caption1" color="text-high-emphasis" className="fw-bold d-block mb-0" style={{ fontSize: 12, lineHeight: 1.4 }}>
                                {t(RULE_BUILDER_SIDEBAR_NESTED_TITLE, 'Nested group')}
                            </Typography>
                            <Typography variant="caption1" color="text-medium-emphasis" className="mb-0" style={{ fontSize: 12, lineHeight: 1.4 }}>
                                {t(RULE_BUILDER_SIDEBAR_NESTED_DESC, 'A group inside another group')}
                            </Typography>
                        </div>
                    </div>
                    <Typography variant="caption1" color="text-medium-emphasis" className="d-block mt-2 mb-0" style={{ fontSize: 12 }}>
                        {t(RULE_BUILDER_SIDEBAR_NESTED_DEPTH, 'Groups can be nested multiple levels deep.')}
                    </Typography>
                </div>
            )}
        </div>
    );
};

export default RuleSidebar;
